import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  setDoc,
} from 'firebase/firestore';

import {
  ref as refDB,
  set,
  onValue,
  query as dbQuery,
  orderByChild,
  push,
  get,
  limitToFirst,
  limitToLast as limitToLastDB,
  equalTo,
  child,
} from 'firebase/database';

import { getAuth } from 'firebase/auth';

import {
  Feedback,
  Comment,
  User,
  AppFeedbackType,
} from '@breakroom-nx/classes';

import { FeedbackType, EntityBeingReported } from '@breakroom-nx/helpers';
import { firestore, database, functions } from '@breakroom-nx/apis';
import { httpsCallable } from 'firebase/functions';

export interface Metadata {
  version: string;
  roomCount: number;
  registeredUsersCount: number;
  unregisteredUsersCount: number;
}

const apiName = '[FEEDBACK API]';
export const feedbackAPI = createApi({
  reducerPath: 'feedbackApi',
  keepUnusedDataFor: 3600,
  baseQuery: fakeBaseQuery<Error>(),
  tagTypes: ['Feedback', 'Updates', 'Comment'],
  endpoints: (builder) => ({
    getAppFeedback: builder.query<Feedback[], void>({
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uid }) => ({
                type: 'Feedback' as const,
                uid,
              })),
              'Feedback',
            ]
          : ['Feedback'],

      async queryFn() {
        try {
          // console.info(`${apiName} Getting app feedback (Realtime Database)`);
          return {
            data: await new Promise((resolve, reject) => {
              const feedbackRef = refDB(database, 'suggestions');
              const feedbackQuery = dbQuery(
                feedbackRef,
                orderByChild('createdAt'),
                limitToLastDB(50)
              );

              // console.log(`Feedback got this far`);

              onValue(
                feedbackQuery,
                (snapshot) => {
                  let feedback: Feedback[] = [];
                  snapshot.forEach((doc) => {
                    // console.log(`Feedback: ${doc.val()}`);
                    const raw = doc.val();

                    const feedbackDoc = new Feedback({
                      uid: doc.key || undefined,
                      feedback: raw.feedback ?? { upvotes: 0, downvotes: 0 },
                      createdAt: raw.createdAt
                        ? new Date(raw.createdAt)
                        : new Date(),
                      content: raw.content,
                      creatorUID: raw.creatorUID,
                      implementationState: raw.implementationState,
                    });

                    // If you have a Feedback class/constructor, use it here
                    feedback.push(feedbackDoc);
                  });
                  // Sort descending by upvotes (since limitToLast returns ascending)
                  feedback.sort(
                    (a, b) =>
                      (b.feedback.upvotes ?? 0) - (a.feedback.upvotes ?? 0)
                  );
                  resolve(feedback);
                },
                (error) => {
                  reject(
                    new Error(
                      `Unable to get app suggestions at this time. Error: ${error.message}`
                    )
                  );
                }
              );
            }),
          };
        } catch (err) {
          // console.log(
          //   `${apiName} (ERROR) Unable to get app feedback. Error: ${err}`
          // );
          throw new Error('Unable to get app suggestions at this time');
        }
      },
    }),

    getFeedbackComments: builder.query<Comment[], { uid: String }>({
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uid }) => ({
                type: 'Comment' as const,
                uid,
              })),
              'Comment',
            ]
          : ['Comment'],

      async queryFn(arg) {
        try {
          // console.info(
          //   `${apiName} Getting app feedback comments for UID: ${arg.uid}`
          // );
          return {
            data: await new Promise((resolve, reject) => {
              const feedbackCommentsRef = refDB(
                database,
                `suggestion-comments/${arg.uid}`
              );
              const feedbackCommentsQuery = dbQuery(
                feedbackCommentsRef,
                orderByChild('createdAt'),
                limitToLastDB(50)
              );

              // console.log(`Feedback Comments got this far`);

              onValue(
                feedbackCommentsQuery,
                (snapshot) => {
                  let feedbackComments: Comment[] = [];
                  snapshot.forEach((doc) => {
                    // console.log(`Feedback Comment: ${doc.val()}`);
                    const raw = doc.val();

                    const feedbackCommentDoc = new Comment({
                      uid: doc.key || undefined,
                      // upvotes: raw.upvotes ?? 0,
                      // downvotes: raw.downvotes ?? 0,
                      createdAt: raw.createdAt
                        ? new Date(raw.createdAt)
                        : new Date(),
                      comment: raw.comment,
                      creatorUID: raw.creatorUID,
                    });

                    // console.log(`Comment: ${feedbackCommentDoc.comment}`);

                    // If you have a Feedback class/constructor, use it here
                    feedbackComments.push(feedbackCommentDoc);
                  });
                  // Sort descending by upvotes (since limitToLast returns ascending)
                  // feedbackComments.sort((a, b) => (b.upvotes ?? 0) - (a.upvotes ?? 0));
                  resolve(feedbackComments);
                },
                (error) => {
                  reject(
                    new Error(
                      `Unable to get suggestion comments at this time. Error: ${error.message}`
                    )
                  );
                }
              );
            }),
          };
        } catch (err) {
          // console.log(
          //   `${apiName} (ERROR) Unable to get suggestion comments. Error: ${err}`
          // );
          throw new Error('Unable to get suggestion comments at this time');
        }
      },
    }),

    getAppUpdates: builder.query<Feedback[], void>({
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uid }) => ({
                type: 'Updates' as const,
                uid,
              })),
              'Updates',
            ]
          : ['Updates'],
      async queryFn() {
        try {
          // console.info(`${apiName} Getting app updates`);

          return {
            data: await new Promise((resolve, reject) => {
              const updatesRef = refDB(database, 'updates');
              const updatesQuery = dbQuery(
                updatesRef,
                orderByChild('createdAt'),
                limitToLastDB(50)
              );

              // console.log(`Updates got this far`);

              onValue(
                updatesQuery,
                (snapshot) => {
                  let updates: Feedback[] = [];
                  snapshot.forEach((doc) => {
                    // console.log(`Update: ${doc.val()}`);
                    const raw = doc.val();

                    const updateDoc = new Feedback({
                      uid: doc.key || undefined,
                      feedback: raw.feedback ?? { upvotes: 0, downvotes: 0 },
                      createdAt: raw.createdAt
                        ? new Date(raw.createdAt)
                        : new Date(),
                      content: raw.content,
                      creatorUID: raw.creatorUID,
                      implementationState: raw.implementationState,
                    });

                    updates.push(updateDoc);
                  });
                  // Sort descending by timestamp
                  updates.sort(
                    (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
                  );
                  resolve(updates);
                },
                (error) => {
                  reject(
                    new Error(
                      `Unable to get app updates at this time. Error: ${error.message}`
                    )
                  );
                }
              );
            }),
          };
        } catch (err) {
          // console.log(
          //   `${apiName} (ERROR) Unable to get unread app updates amount. Error: ${err}`
          // );
          throw new Error('Unable to get app updates at this time');
        }
      },
    }),

    // Mutations
    createFeedback: builder.mutation<
      null,
      { user: User | undefined; feedback: Feedback; type: FeedbackType }
    >({
      invalidatesTags: ['Feedback'],
      async queryFn({ user, feedback, type }) {
        try {
          if (!user) throw new Error(`No user found`);
          if (!feedback) throw new Error(`No feedback provided`);

          // console.log(`${apiName} Adding new app feedback.`);

          const feedbackRef = refDB(database, `suggestions`);
          push(feedbackRef, feedback);

          return { data: null };
        } catch (err) {
          // console.log(`${apiName} Error creating feedback: ${err}`);
          throw new Error('Error uploading feedback');
        }
      },
    }),

    voteOnFeedback: builder.mutation<
      null,
      {
        user: User | undefined;
        feedbackID: string;
        type: AppFeedbackType;
        upvote: boolean;
      }
    >({
      invalidatesTags: ['Feedback'],
      async queryFn({ user, feedbackID, type, upvote }) {
        try {
          if (!user) throw new Error(`No user found`);
          if (!feedbackID) throw new Error(`No feedback ID provided`);

          // console.log(`${apiName} Voting on feedback.`);

          const path =
            type === AppFeedbackType.Suggestions
              ? `suggestions/${feedbackID}`
              : `updates/${feedbackID}`;

          const feedbackRef = doc(firestore, path).withConverter(
            feedbackConverter
          );

          // Update Vote
          const votesCollectionRef = collection(
            firestore,
            `${feedbackRef.path}/votes`
          );

          const voteDocRef = doc(
            firestore,
            `${votesCollectionRef.path}/${user.uid}`
          );

          const feedbackDoc = getDoc(feedbackRef);
          const feedbackData = (await feedbackDoc).data();
          const currentUpvotes = feedbackData?.upvotes ?? 0;
          const currentDownvotes = feedbackData?.downvotes ?? 0;

          await getDoc(voteDocRef).then((snapshot) => {
            if (snapshot.exists()) {
              // Document Exists => Modify Vote
              const voteData = snapshot.data();
              if (voteData.upvote === upvote) {
                // Delete Document
                deleteDoc(voteDocRef)
                  .then(() => {
                    if (upvote) {
                      updateDoc(feedbackRef, {
                        upvotes: currentUpvotes - 1,
                      });
                    } else {
                      updateDoc(feedbackRef, {
                        downvotes: currentDownvotes - 1,
                      });
                    }
                  })
                  .catch((err) => {
                    throw new Error(
                      `Error deleting existing vote doc: (${err.errorCode}: ${err.errorMessage})`
                    );
                  });
              } else {
                // Overwrite Document with New Vote
                setDoc(voteDocRef, {
                  timestamp: new Date(),
                  upvote: upvote,
                })
                  .then(() => {
                    if (upvote) {
                      updateDoc(feedbackRef, {
                        downvotes: currentDownvotes - 1,
                      });
                    } else {
                      updateDoc(feedbackRef, {
                        upvotes: currentUpvotes - 1,
                      });
                    }
                  })
                  .catch((err) => {
                    throw new Error(
                      `Error overwriting doc: (${err.errorCode}: ${err.errorMessage})`
                    );
                  });
              }
            } else {
              // Document Doesn't Exist => Create
              setDoc(voteDocRef, {
                timestamp: new Date(),
                upvote: upvote,
              })
                .then(() => {
                  if (upvote) {
                    updateDoc(feedbackRef, {
                      upvotes: currentUpvotes + 1,
                    });
                  } else {
                    updateDoc(feedbackRef, {
                      downvotes: currentDownvotes + 1,
                    });
                  }
                })
                .catch((err) => {
                  throw new Error(
                    `Error setting vote doc: (${err.errorCode}: ${err.errorMessage})`
                  );
                });
            }
          });

          return { data: null };
        } catch (err) {
          // console.log(`${apiName} Error voting on feedback: ${err}`);
          throw new Error('Error voting on feedback');
        }
      },
    }),

    getRating: builder.query<
      {
        upvotes: number;
        downvotes: number;
        ratio: number;
        upvoted: boolean | undefined;
      },
      { uid: string; entityBeingReported: EntityBeingReported } | undefined
    >({
      async queryFn(queryArgs) {
        try {
          const auth = getAuth();
          const currentUserUID = auth.currentUser?.uid;

          // console.info(
          //   `${apiName}: Getting ${
          //     EntityBeingReported[queryArgs?.entityBeingReported || 0]
          //   }'s rating...`
          // );
          if (!queryArgs?.uid) throw new Error('UID cannot be undefined');
          if (!currentUserUID)
            throw new Error('Current User UID cannot be undefined');

          const entityRef = refDB(
            database,
            `${
              queryArgs.entityBeingReported === EntityBeingReported.User
                ? 'users'
                : 'chats'
            }/` +
              queryArgs.uid +
              '/feedback'
          );

          const entitySnap = await get(entityRef);
          const currentUserVoteSnap = await get(
            child(entityRef, `votes/${currentUserUID}`)
          );

          if (entitySnap.exists()) {
            const data = entitySnap.val();
            const upvotes = data.upvotes ?? 0;
            const downvotes = data.downvotes ?? 0;

            // console.log(`Upvotes: ${upvotes} | Downvotes: ${downvotes}`);
            const ratio = (upvotes / (upvotes + downvotes)) * 100;

            if (currentUserVoteSnap.exists()) {
              const voteData = currentUserVoteSnap.val();
              const voteDirection = voteData.upvote;

              // console.log(`Voted: ${voteDirection} | Positive Ratio: ${ratio}`);
              return {
                data: {
                  upvotes: upvotes ?? 0,
                  downvotes: downvotes ?? 0,
                  ratio: ratio,
                  upvoted: voteDirection,
                },
              };
            } else {
              return {
                data: {
                  upvotes: upvotes ?? 0,
                  downvotes: downvotes ?? 0,
                  ratio: ratio,
                  upvoted: undefined,
                },
              };
            }
          } else {
            throw new Error('Entity feedback document does not exist');
          }
        } catch (err) {
          // console.log(
          //   `${apiName} (Error) Unable to get entity's reviews: ${err}`
          // );
          throw new Error("Unable to get the entity's feedback");
        }
      },
    }),

    getAppMetadata: builder.query<Metadata, null>({
      async queryFn() {
        try {
          // console.log(`${apiName} Getting app metadata`);
          const metadataRef = doc(firestore, 'app', 'metadata');
          const metaDataSnap = await getDoc(metadataRef);
          if (metaDataSnap.exists()) {
            const data = metaDataSnap.data();
            const metadataDoc: Metadata = {
              version: data['version'],
              roomCount: data['roomCount'],
              registeredUsersCount: data['registeredUsersCount'],
              unregisteredUsersCount: data['unregisteredUsersCount'],
            };

            return { data: metadataDoc };
          } else {
            throw new Error('Metadata document does not exist');
          }
        } catch (err) {
          // console.log(`${apiName} (Error) Error getting app metadata: ${err}`);
          throw new Error('Unable to get app metadata at this time');
        }
      },
    }),

    createComment: builder.mutation<
      null,
      { user: User | undefined; feedbackID: string; comment: string }
    >({
      invalidatesTags: ['Comment'],
      async queryFn({ user, feedbackID, comment }) {
        try {
          if (!user) throw new Error(`No user found`);
          if (!feedbackID) throw new Error(`No feedback ID provided`);
          if (!comment) throw new Error(`No comment provided`);

          // console.log(`${apiName} Adding new feedback comment.`);

          const feedbackCommentsRef = refDB(
            database,
            `suggestion-comments/${feedbackID}`
          );

          // Push the message to the messages list
          push(feedbackCommentsRef, {
            comment: comment,
            createdAt: new Date().toISOString(),
            creatorUID: user.uid,
          });

          return { data: null };
        } catch (err) {
          // console.log(`${apiName} Error creating comment: ${err}`);
          throw new Error('Error uploading comment');
        }
      },
    }),
  }),
});

export const {
  useGetAppFeedbackQuery,
  useGetAppUpdatesQuery,
  useGetRatingQuery,
  useGetAppMetadataQuery,
  useCreateFeedbackMutation,
  useVoteOnFeedbackMutation,
  useLazyGetFeedbackCommentsQuery,
  useCreateCommentMutation,
} = feedbackAPI;
